import {
  ADMIN_CHAT,
  AdminHeader,
  AdminOptionsDropdown,
  AdminSpinner,
  AdminWalletBankResponse,
  BankV2,
  CHAT_CONVERSATION_PARAM,
  ChatIcon,
  ClientPaymentsIconDD,
  ConnectYourBank,
  Disclaimer,
  Div,
  DropdownDeleteIcon,
  DropdownNotesIcon,
  H3,
  H5,
  LocationsPinRedIcon,
  PhoneNumberText,
  Pill,
  RecoverIcon,
  Separator,
  TooltipIcon,
  UserTicketRedIcon,
  WalletBankResponse,
  WalletBankV2Response,
  Wallets,
  displayCentsInDollars,
  getAgentDetails,
  getUserBankAndBalanceInfo,
  lightTheme,
  openSupportConversation,
  renderRole,
  useAuthenticatedAuthUser,
  usePermissions,
  UserPermission,
  useUsers,
  reportError,
} from '@vgrubs/components';
import { AgentDetailsResponse } from 'components/src/types/api/agents.types';
import { User, UserRole } from 'components/src/types/user.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { KeyValueInfo } from '../../Verifications/components/KeyValueInfo';
import { SectionCard } from '../../Verifications/components/SectionCard';
import { UserAgentEditPendingEarnings } from '../Bank/UserAgentEditPendingEarnings';
import { UserAgentPendingEarningsCreditCard } from '../Bank/UserAgentPendingEarningsCreditCard';
import { UserCreditCard } from '../Bank/UserCreditCard';
import { UserCreditCardDelete } from '../Bank/UserCreditCardDelete';
import { UserCreditCardEditBalance } from '../Bank/UserCreditCardEditBalance';
import { UsersAndAccessDeleteModal } from '../UsersAndAccessDelete';
import { UsersAndAccessEditModal } from '../UsersAndAccessEdit';

// Import Swiper styles
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { UsersAndAccessRecoverModal } from '../UsersAndAccessRecover';

export const AgentEarningLevel = ({ level }) => {
  if (level) {
    return (
      <Div $flex $alignCenter $gap="6px">
        <Pill $background={lightTheme.primary}>
          <Disclaimer $m="0" $color={lightTheme.white}>
            Level {level.level}
          </Disclaimer>
        </Pill>
        <TooltipIcon
          tooltip={`Your current commission rate is ${displayCentsInDollars(
            level.price
          )}. To increase your commission, you need to unlock the next level. ${
            level.level < 4 &&
            `At present, you are ${
              level.nextLevelCount
            } locations away from reaching Level ${
              level.level + 1
            }, which offers a commission rate of ${displayCentsInDollars(
              level.nextLevelAmount
            )}.`
          }`}
        />
      </Div>
    );
  } else return null;
};

//

export const UserDetailsAgent = () => {
  const [loading, setLoading] = useState(false);
  const [agentDetails, setAgentDetails] = useState<AgentDetailsResponse | null>(
    null
  );

  const { invalidateByPrefix } = useInvalidateQueries();

  const { role } = useAuthenticatedAuthUser();
  const { can } = usePermissions();

  const agent = agentDetails?.agent;
  const user = agentDetails?.user;

  /**
   * Talk to the agent, from the same place every other person in this app is
   * reached from.
   *
   * A sales agent is a SUPPORT thread, not a direct message: the backend puts
   * them on the outside of the queue alongside merchants, in their own tab. So
   * this needs the same write permission answering a merchant does, and the
   * link names that tab - landing on a thread the list beside it does not
   * contain looks like the page failed to select anything.
   *
   * The endpoint is idempotent, so there is nothing to check first and no way
   * to end up with two threads for one agent.
   */
  const canOpenConversation =
    !!user &&
    !user.deleted &&
    can(UserPermission.conversationRead) &&
    can(UserPermission.conversationWrite);

  const openConversation = async () => {
    if (!user) return;
    try {
      const { data } = await openSupportConversation(user.id);
      navigate(
        `${ADMIN_CHAT}?scope=sales&${CHAT_CONVERSATION_PARAM}=${data.data.id}`
      );
    } catch (error) {
      reportError(error);
    }
  };

  const {
    loading: loadingBank,

    setBank,
    bank,
    setEditBalanceModal,
    editBalanceModal,
    setDeleteBankModal,
    deleteBankModal,
    addBankModal,
    setAddBankModal,
    connectYourBankModal,
    setConnectYourBankModal,
  } = useUsers((state) => state);

  const [editModal, setEditModal] = useState<User | null>(null);
  const [deleteModal, setDeleteModal] = useState<User | null>(null);
  const [recoverModal, setRecoverModal] = useState<User | null>(null);
  const { id } = useParams() as { id: string };

  const navigate = useNavigate();

  async function handleGetAgentDetails(silent = false) {
    try {
      if (!silent) {
        setLoading(true);
      }
      const { data } = await getAgentDetails(id);
      setAgentDetails(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      handleGetAgentDetails();
      getBankByUserId();
    }
  }, []);

  const handleOnEdit = (user) => {
    setEditModal(user);
  };

  const handleOnDelete = (user) => {
    setDeleteModal(user);
  };

  const handleOnLocations = () => {
    navigate(`/users/agent/${id}/locations`);
  };

  const handleOnAgent = () => {
    navigate(`/users/agent/${id}/agents`);
  };

  async function getBankByUserId() {
    try {
      setLoading(true);
      const { data } = await getUserBankAndBalanceInfo(id);

      setBank(data.data);

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const handleRecoverUser = (user: User) => {
    setRecoverModal(user);
  };

  const actions = [
    {
      label: 'Edit User',
      onClick: () => setEditModal(user || null),
      Icon: DropdownNotesIcon,
    },
    ...(canOpenConversation
      ? [
          {
            label: 'Open Conversation',
            onClick: openConversation,
            Icon: ChatIcon,
          },
        ]
      : []),
    {
      label: 'Transactions',
      onClick: () =>
        navigate(`/transactions/agent/${agent?.id}`, { state: user }),
      Icon: ClientPaymentsIconDD,
    },
    {
      label: user?.deleted ? 'Recover User' : 'Delete User',
      onClick: () => {
        if (user?.deleted) {
          setRecoverModal(user);
        } else {
          setDeleteModal(user || null);
        }
      },
      Icon: user?.deleted ? RecoverIcon : DropdownDeleteIcon,
      color: lightTheme.primary,
    },
  ];

  return (
    <>
      {(loading || loadingBank) && <AdminSpinner />}

      {agentDetails && (
        <>
          <AdminHeader title={'Agent Details'}>
            <Div $flex $alignCenter $gap="20px" $gapSM="10px">
              <AgentEarningLevel level={agent?.level} />
              <AdminOptionsDropdown actions={actions} />
            </Div>
          </AdminHeader>
          <Div $flex $column $gap="8px" $m="32px 0" $mSM="20px 0" $width="100%">
            <H3 $mb={0}>
              <span>{user?.firstName + ' ' + user?.lastName}</span>
            </H3>
            {user?.deleted && (
              <div>
                <Pill $background={lightTheme.darkGray}>
                  <Disclaimer $m="0" $color={lightTheme.white}>
                    Inactive
                  </Disclaimer>
                </Pill>
              </div>
            )}
          </Div>
          <Div $flex $alignStart $columnSM $gap="32px" $gapSM="20px">
            <Div $width="50%" $widthSM="100%" $flex $column $gap="20px">
              <SectionCard title="Profile">
                {user?.email && (
                  <a href={`mailto:${user.email}`}>
                    <KeyValueInfo k="Email" v={user.email} />
                  </a>
                )}
                {user?.phoneNumber && (
                  <KeyValueInfo
                    k="Phone"
                    v={user.phoneNumber}
                    component={
                      <PhoneNumberText phone={user.phoneNumber} title />
                    }
                  />
                )}
                <KeyValueInfo
                  k="Role"
                  v={renderRole(user?.role || null)}
                  last
                />
              </SectionCard>

              {/* AGENT BANK */}
              {role === 'admin' && (
                <Div $mt="12">
                  <H3 $mb="16">vPay Balance</H3>
                  <Div $width="100%" $maxWidth="400px">
                    <Swiper
                      modules={[Pagination]}
                      spaceBetween={50}
                      slidesPerView={1}
                      pagination={{ clickable: true }}
                      style={
                        {
                          '--swiper-pagination-color': lightTheme.blue,
                          '--swiper-pagination-bullet-inactive-color':
                            lightTheme.lightGray,
                          '--swiper-pagination-bullet-inactive-opacity': '1',
                          '--swiper-pagination-bullet-size': '10px',
                          '--swiper-pagination-bullet-horizontal-gap': '11px',
                        } as React.CSSProperties
                      }
                    >
                      <SwiperSlide>
                        <Div $p=" 0 0 40px 0" $flex>
                          <UserCreditCard id={id} isAgent />
                        </Div>
                      </SwiperSlide>
                      <SwiperSlide>
                        <Div $p="0 0 40px 0" $flex>
                          <UserAgentPendingEarningsCreditCard />
                        </Div>
                      </SwiperSlide>
                    </Swiper>
                  </Div>
                </Div>
              )}
              {/* AGENT BANK */}
            </Div>
            <Div $width="50%" $widthSM="100%">
              <SectionCard title="Earnings">
                <KeyValueInfo
                  k="Flat amount per location"
                  v={displayCentsInDollars(agent?.amountPerLocation || null)}
                />
                {[
                  {
                    Icon: LocationsPinRedIcon,
                    title: 'Signup Locations',
                    invited: agentDetails?.invitedLocationsCount,
                    count: agentDetails?.signedLocationsCount,
                    onClick: handleOnLocations,
                  },
                  {
                    Icon: UserTicketRedIcon,
                    title: 'Agent List',
                    invited: agentDetails?.invitedAgentsCount,
                    count: agentDetails?.verifiedAgentsCount,
                    onClick: handleOnAgent,
                  },
                ].map(({ Icon, title, invited, count, onClick }, i) => (
                  <div key={title}>
                    {i > 0 && <Separator />}
                    <Div
                      $flex
                      $between
                      $alignCenter
                      $gap="16px"
                      $p={i > 0 ? '16px 0 0 0' : '16px 0'}
                      $pointer
                      onClick={onClick}
                    >
                      <Div $flex $gap="16px" $alignCenter>
                        <Icon />
                        <div>
                          <H5 $mb="4">{title}</H5>
                          <Disclaimer $m="0" $color={lightTheme.lightGray}>
                            Total invite: {invited ?? 0}
                          </Disclaimer>
                        </div>
                      </Div>
                      <H3 $m="0">
                        <span>{count ?? 0}</span>
                      </H3>
                    </Div>
                  </div>
                ))}
              </SectionCard>
            </Div>
          </Div>
        </>
      )}

      <BankV2
        userRole={user?.role as UserRole}
        userId={Number(id)}
        modalIsOpen={addBankModal}
        setModalIsOpen={
          setAddBankModal as Dispatch<SetStateAction<Wallets | boolean | null>>
        }
        wallet={bank as WalletBankResponse | AdminWalletBankResponse}
        setWallet={
          setBank as Dispatch<
            SetStateAction<
              | WalletBankV2Response
              | AdminWalletBankResponse
              | WalletBankResponse
              | null
            >
          >
        }
        user={user}
      />
      <ConnectYourBank
        isOpen={connectYourBankModal}
        setIsOpen={
          setConnectYourBankModal as Dispatch<
            SetStateAction<Wallets | boolean | null>
          >
        }
        setModalIsOpen={
          setAddBankModal as Dispatch<SetStateAction<Wallets | boolean | null>>
        }
      />

      {/* <UserCreditCardAdd id={user?.id} /> */}
      <UserCreditCardDelete
        userId={id}
        getBank={getBankByUserId}
        deleteBankModal={deleteBankModal}
        setDeleteBankModal={
          setDeleteBankModal as (v: Wallets | boolean | null) => void
        }
        setLoading={setLoading}
        userRole={user?.role as UserRole}
      />
      <UserCreditCardEditBalance
        userId={Number(id)}
        getBank={getBankByUserId}
        editBalanceModal={editBalanceModal}
        setEditBalanceModal={
          setEditBalanceModal as (v: Wallets | boolean | null) => void
        }
        userRole={user?.role as UserRole}
        setLoading={setLoading}
      />
      <UserAgentEditPendingEarnings id={user?.id} />

      <UsersAndAccessEditModal
        modalIsOpen={editModal}
        closeModal={() => setEditModal(null)}
        setLoading={setLoading}
        isAgent
        agentAmountPerLocation={agent?.amountPerLocation || undefined}
        setAgentDetails={setAgentDetails}
        onSuccess={() => {
          invalidateByPrefix('users');
        }}
      />

      <UsersAndAccessDeleteModal
        setLoading={setLoading}
        closeModal={() => setDeleteModal(null)}
        modalIsOpen={deleteModal}
        onSuccess={() => {
          setDeleteModal(null);
          handleGetAgentDetails(true);
          invalidateByPrefix('users');
        }}
      />

      <UsersAndAccessRecoverModal
        setLoading={setLoading}
        closeModal={() => setRecoverModal(null)}
        modalIsOpen={recoverModal}
        onSuccess={() => {
          setRecoverModal(null);
          handleGetAgentDetails(true);
          invalidateByPrefix('users');
        }}
      />
    </>
  );
};
